import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import type { AspectRatio } from '@lumen5/luminary';

import type { CreateVideoFromPlanArgs } from 'js/src/agent/tools/CreateVideoFromPlanTool';
import type { CreateVideoFromVoiceoverAssetPlanArgs } from 'js/src/agent/tools/CreateVideoFromVoiceoverAssetPlanTool';
import type { AIVoice, Blueprint } from 'js/src/client';
import { NarrativeThreadEnum, Urls } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import TextInput, {
    FEEDBACK_MESSAGE_TYPES,
    TEXT_INPUT_VARIANTS,
} from 'js/src/components/common/ui/TextInput';
import { SketchWidget } from 'js/src/components/sketch/follow-on-actions/SketchWidget';
import { useExecuteTool } from 'js/src/components/sketch/SketchContext';
import type {
    ClearableSketchControl,
    SketchControl,
} from 'js/src/components/sketch/SketchControls';
import { SketchControls } from 'js/src/components/sketch/SketchControls';
import {
    BADGE_SIZES,
    type SketchControlsBadgesState,
} from 'js/src/components/sketch/SketchControlsBadges';
import { getCanUseAIVoiceover, getCanUsePremiumAIVoices } from 'js/src/data/account';
import { ensureAIVoiceLoaded, getAIVoiceById } from 'js/src/data/ai-voices-and-avatars';
import { getOrFetchBlueprint } from 'js/src/data/blueprints';
import { getAllBrandKits, getBrandKitColorsInHex } from 'js/src/data/brand-kits';
import {
    resetMediaSourcesToDefaultsOperation,
    selectDamMediaEnabled,
    selectMediaGuidance,
    selectStockMediaEnabled,
    selectWorkspaceMediaMode,
} from 'js/src/data/media-recommendation-preferences';
import { saveSketchControlsToCookie } from 'js/src/data/sketch-controls/sketch-controls-cookies';
import { selectSketchControlsBrandKit } from 'js/src/data/sketch-controls/sketch-controls-selectors';
import { clearBrandKit, setBrandKit } from 'js/src/data/sketch-controls/sketch-controls-slice';
import {
    getSupportedAspectRatios,
    getTemplateById,
    getTemplatePaletteColorCodes,
} from 'js/src/data/templates/templates-selectors';
import {
    getIncompatiblePropertyLabel,
    getVideoPlanIncompatibilities,
    type PropertyChange,
    type VideoPlanState,
} from 'js/src/data/video-planner/video-plan-compatibility';
import type {
    VideoPlanWithScript,
    VideoPlanWithVoiceoverAsset,
    VoiceoverAsset,
} from 'js/src/data/video-planner/video-planner-types';
import { int2rgb } from 'js/src/libs/css-helpers';
import Flags from 'js/src/libs/flags';
import Navigation from 'js/src/libs/navigation';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';
import type { StoredTemplate } from 'js/src/data/templates';

export interface VideoPlanEditorProps {
    videoPlan: VideoPlanWithScript | VideoPlanWithVoiceoverAsset;
}

function hasVoiceoverAsset(
    plan: VideoPlanWithScript | VideoPlanWithVoiceoverAsset,
): plan is VideoPlanWithVoiceoverAsset {
    return 'voiceover_asset' in plan && !!plan.voiceover_asset;
}

export interface VideoPlanEditorState {
    script: string;
    aspectRatio: AspectRatio | null;
    templateId: string | null;
    narrativeThread: NarrativeThreadEnum | null;
    voiceSlug: string | null;
    durationSeconds: number | null;
    blueprintId: number | null;
}

const FALLBACK_TEMPLATE_ID = 'assembly';

function resolveTemplateAndAspectRatio(
    editorState: VideoPlanEditorState,
    originalPlan: VideoPlanWithScript,
    blueprint: Blueprint | null,
    getSupportedRatios: (templateId: string) => AspectRatio[],
): { templateId: string; aspectRatio: AspectRatio } {
    // Fill nulls with fallbacks
    const aspectRatio = editorState.aspectRatio ?? (originalPlan.aspect_ratio as AspectRatio);
    const templateId =
        editorState.templateId ??
        (blueprint ? blueprint.default_template_id : originalPlan.template_id);

    // Both explicitly set or compatible — done
    if (
        (editorState.templateId && editorState.aspectRatio) ||
        getSupportedRatios(templateId).includes(aspectRatio)
    ) {
        return { templateId, aspectRatio };
    }

    // Incompatible: explicit aspect ratio takes priority — find compatible template
    if (editorState.aspectRatio) {
        if (getSupportedRatios(originalPlan.template_id).includes(aspectRatio)) {
            return { templateId: originalPlan.template_id, aspectRatio };
        }
        return { templateId: FALLBACK_TEMPLATE_ID, aspectRatio };
    }

    // Incompatible: template takes priority — pick compatible ratio
    return { templateId, aspectRatio: getSupportedRatios(templateId)[0] };
}

export function fillMissingVideoPlanFields(
    editorState: VideoPlanEditorState,
    originalPlan: VideoPlanWithScript,
    blueprint: Blueprint | null,
    getSupportedRatios: (templateId: string) => AspectRatio[],
): VideoPlanWithScript {
    const narrativeThread =
        editorState.narrativeThread ??
        (blueprint ? blueprint.narrative_thread : originalPlan.narrative_thread);

    const { templateId, aspectRatio } = resolveTemplateAndAspectRatio(
        editorState,
        originalPlan,
        blueprint,
        getSupportedRatios,
    );

    return {
        id: originalPlan.id,
        title: originalPlan.title,
        content_prompt: originalPlan.content_prompt,
        video_prompt: originalPlan.video_prompt,
        script: editorState.script,
        aspect_ratio: aspectRatio,
        template_id: templateId,
        narrative_thread: narrativeThread,
        blueprint_id: editorState.blueprintId,
        duration_target_seconds: editorState.durationSeconds,
        ai_voice:
            narrativeThread === NarrativeThreadEnum.VOICEOVER
                ? (editorState.voiceSlug ?? originalPlan.ai_voice)
                : null,
        ai_avatar: null,
    };
}

const toNarrativeThreadEnum = (value: string): NarrativeThreadEnum | null => {
    const enumValues = Object.values(NarrativeThreadEnum) as string[];
    return enumValues.includes(value) ? (value as NarrativeThreadEnum) : null;
};

export const VideoPlanEditor: React.FC<VideoPlanEditorProps> = ({ videoPlan }) => {
    const dispatch = useAppDispatch();
    const executeTool = useExecuteTool();
    const store = useStore<RootState>();
    const originalPlan = useRef(videoPlan);
    const voiceoverAsset: VoiceoverAsset | null = hasVoiceoverAsset(videoPlan)
        ? videoPlan.voiceover_asset
        : null;

    const [collapsed, setCollapsed] = useState(false);
    const [hasCreatedVideo, setHasCreatedVideo] = useState(false);
    const [script, setScript] = useState(videoPlan.script);
    const [scriptError, setScriptError] = useState<string>('');
    const [aspectRatio, setAspectRatio] = useState<AspectRatio | null>(
        videoPlan.aspect_ratio as AspectRatio,
    );
    const [templateId, setTemplateId] = useState<string | null>(videoPlan.template_id);
    const [narrativeThread, setNarrativeThread] = useState<NarrativeThreadEnum | null>(
        toNarrativeThreadEnum(videoPlan.narrative_thread),
    );
    const [voiceSlug, setVoiceSlug] = useState<string | null>(videoPlan.ai_voice ?? null);
    const [durationSeconds, setDurationSeconds] = useState<number | null>(
        videoPlan.duration_target_seconds ?? null,
    );
    const [blueprintId, setBlueprintId] = useState<number | null>(videoPlan.blueprint_id ?? null);
    const [blueprint, setBlueprint] = useState<Blueprint | null>(null);

    const voice = useSelector((state: RootState) =>
        voiceSlug ? getAIVoiceById(state, voiceSlug) : null,
    );

    const template = useSelector((state: RootState) =>
        templateId ? getTemplateById(state, templateId) : null,
    );

    const selectedBrandKit = useSelector(selectSketchControlsBrandKit);
    const selectedBrandKitId = selectedBrandKit?.id ?? null;
    const brandKitColors = useSelector((state: RootState) =>
        selectedBrandKitId ? getBrandKitColorsInHex(state, selectedBrandKitId) : null,
    );
    const templatePaletteColors = useSelector((state: RootState) =>
        templateId ? getTemplatePaletteColorCodes(state, templateId) : null,
    );
    const brandKits = useSelector(getAllBrandKits);
    const stockMediaEnabled = useSelector(selectStockMediaEnabled);
    const damMediaEnabled = useSelector(selectDamMediaEnabled);
    const workspaceMediaMode = useSelector(selectWorkspaceMediaMode);
    const mediaGuidance = useSelector(selectMediaGuidance);
    const canUseAIVoiceover = useSelector(getCanUseAIVoiceover);
    const supportedAspectRatios = useSelector((state: RootState) =>
        templateId ? getSupportedAspectRatios(state, templateId) : null,
    );

    const isDamAvailable = Flags.canSeeDamTab();

    const voiceName = voice?.display_name ?? null;
    const templateName = template?.display_name || null;
    const brandKitName = selectedBrandKit?.name ?? null;

    React.useEffect(() => {
        if (!blueprintId) {
            return;
        }
        dispatch(getOrFetchBlueprint(blueprintId)).then((loadedBlueprint) => {
            if (loadedBlueprint) {
                setBlueprint(loadedBlueprint);
            } else {
                setBlueprintId(null);
            }
        });
    }, [blueprintId, dispatch]);

    const validateScript = useCallback((script: string) => {
        if (!script.trim()) {
            setScriptError('A script is required to create a video.');
            return false;
        }
        return true;
    }, []);

    const handleScriptChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setScript(e.target.value);
            if (scriptError) setScriptError('');
        },
        [scriptError],
    );

    const clearIncompatibleSettings = useCallback(
        (change: PropertyChange, stateOverride?: Partial<VideoPlanState>) => {
            // Accept an optional state override to avoid stale closure values.
            // React batches state updates, so useState values are not yet
            // updated when this is called from a handler that just called
            // multiple setState functions (e.g. handleSelectBlueprint).
            const currentState: Partial<VideoPlanState> = stateOverride ?? {
                aspectRatio,
                template,
                blueprint: blueprint ?? null,
                narrativeThread,
                voiceSlug,
            };
            const incompatibilities = getVideoPlanIncompatibilities(change, currentState);
            for (const incompatibility of incompatibilities) {
                const label = getIncompatiblePropertyLabel(incompatibility.property);
                Toast.info(
                    `${label} is not compatible with selected ${change.type}. ${label} has been cleared.`,
                );
                switch (incompatibility.property) {
                    case 'aspectRatio':
                        setAspectRatio(null);
                        break;
                    case 'template':
                        setTemplateId(null);
                        break;
                    case 'voice':
                        setVoiceSlug(null);
                        break;
                    case 'blueprint':
                        setBlueprint(null);
                        setBlueprintId(null);
                        break;
                    case 'narrativeThread':
                        setNarrativeThread(null);
                        break;
                    default: {
                        const _exhaustive: never = incompatibility.property;
                        throw new Error(`Unhandled control: ${_exhaustive}`);
                    }
                }
            }
        },
        [aspectRatio, blueprint, narrativeThread, template, voiceSlug],
    );

    const handleSelectBlueprint = useCallback(
        async (blueprint: Blueprint) => {
            setBlueprintId(blueprint.id);

            // Blueprint sets template, narrative thread, and aspect ratio
            setTemplateId(blueprint.default_template_id);
            setNarrativeThread(blueprint.narrative_thread);
            setAspectRatio(blueprint.default_aspect_ratio);

            // Set voice if blueprint has one (for voiceover blueprints)
            // Only set voice if it's available to the user (falls back to auto)
            let newVoiceSlug: string | null = null;
            if (blueprint.ai_voice) {
                const voice =
                    getAIVoiceById(store.getState(), blueprint.ai_voice) ??
                    (await dispatch(ensureAIVoiceLoaded(blueprint.ai_voice)));
                const canUsePremium = getCanUsePremiumAIVoices(store.getState());
                const isVoiceAvailable =
                    voice && !voice.is_deprecated && (!voice.is_paid || canUsePremium);

                if (isVoiceAvailable) {
                    newVoiceSlug = blueprint.ai_voice;
                    setVoiceSlug(blueprint.ai_voice);
                }
            } else {
                setVoiceSlug(null);
            }

            // Set duration from blueprint if available
            if (blueprint.default_target_duration_seconds) {
                setDurationSeconds(blueprint.default_target_duration_seconds);
            }

            // Pass fresh state to avoid stale closure values from batched setState calls
            const newTemplate =
                getTemplateById(store.getState(), blueprint.default_template_id) ?? null;
            clearIncompatibleSettings(
                { type: 'blueprint', newValue: blueprint },
                {
                    aspectRatio: blueprint.default_aspect_ratio,
                    template: newTemplate,
                    blueprint,
                    narrativeThread: blueprint.narrative_thread,
                    voiceSlug: newVoiceSlug,
                },
            );
        },
        [clearIncompatibleSettings, dispatch, store],
    );

    const handleSelectTemplate = useCallback(
        (template: StoredTemplate) => {
            setTemplateId(template.id);
            clearIncompatibleSettings({ type: 'template', newValue: template });
        },
        [clearIncompatibleSettings],
    );

    const handleSelectVoice = useCallback(
        (voice: AIVoice) => {
            setVoiceSlug(voice.slug);
            setNarrativeThread(NarrativeThreadEnum.VOICEOVER);
            clearIncompatibleSettings(
                {
                    type: 'narrativeThread',
                    newValue: NarrativeThreadEnum.VOICEOVER,
                },
                {
                    aspectRatio,
                    template,
                    blueprint: blueprint ?? null,
                    narrativeThread: NarrativeThreadEnum.VOICEOVER,
                    voiceSlug: voice.slug,
                },
            );
        },
        [aspectRatio, blueprint, clearIncompatibleSettings, template],
    );

    const handleSelectDuration = useCallback((seconds: number) => {
        setDurationSeconds(seconds);
    }, []);

    const handleSelectAspectRatio = useCallback(
        (aspectRatio: AspectRatio) => {
            setAspectRatio(aspectRatio);
            clearIncompatibleSettings({ type: 'aspectRatio', newValue: aspectRatio });
        },
        [clearIncompatibleSettings],
    );

    const handleClearControl = useCallback(
        (control: ClearableSketchControl) => {
            switch (control) {
                case 'blueprint':
                    setBlueprintId(null);
                    setBlueprint(null);
                    setTemplateId(null);
                    setNarrativeThread(null);
                    setVoiceSlug(null);
                    setAspectRatio(null);
                    setDurationSeconds(null);
                    break;
                case 'template':
                    setTemplateId(null);
                    break;
                case 'narrativeThread':
                    setNarrativeThread(null);
                    setVoiceSlug(null);
                    break;
                case 'duration':
                    setDurationSeconds(null);
                    break;
                case 'format':
                    setAspectRatio(null);
                    break;
                case 'mediaSources':
                    dispatch(resetMediaSourcesToDefaultsOperation());
                    break;
                case 'brandKit':
                    dispatch(clearBrandKit());
                    dispatch(saveSketchControlsToCookie());
                    break;
                default: {
                    const _exhaustive: never = control;
                    throw new Error(`Unhandled control: ${_exhaustive}`);
                }
            }
        },
        [dispatch],
    );

    const handleSelectControl = useCallback(
        (control: SketchControl, value?: unknown) => {
            switch (control) {
                case 'blueprint':
                case 'template':
                case 'duration':
                case 'format':
                case 'mediaSources':
                case 'voice':
                case 'project':
                    // Controls aren't supported or are handled by SketchControls directly
                    break;
                case 'narrativeThread': {
                    const thread = value as NarrativeThreadEnum;
                    setNarrativeThread(thread);

                    if (thread === NarrativeThreadEnum.TEXT) {
                        setVoiceSlug(null);
                    }

                    clearIncompatibleSettings({ type: 'narrativeThread', newValue: thread });
                    break;
                }
                case 'brandKit': {
                    const brandKitId = value as number;

                    if (brandKitId === -1) {
                        Navigation.openNewTab(Urls.BRAND_KITS);
                        return;
                    }

                    dispatch(setBrandKit(brandKitId));
                    dispatch(saveSketchControlsToCookie());
                    break;
                }
                default: {
                    const _exhaustive: never = control;
                    throw new Error(`Unhandled control: ${_exhaustive}`);
                }
            }
        },
        [clearIncompatibleSettings, dispatch],
    );

    const handleCreateVideo = useCallback(() => {
        if (!executeTool) return;

        // Voiceover-upload flow: transcript is fixed by the uploaded asset, so
        // skip script validation and dispatch the dedicated build tool.
        if (voiceoverAsset) {
            const validVideoPlan = fillMissingVideoPlanFields(
                {
                    script,
                    aspectRatio,
                    templateId,
                    narrativeThread,
                    voiceSlug,
                    durationSeconds,
                    blueprintId,
                },
                originalPlan.current,
                blueprint,
                (tid) => getSupportedAspectRatios(store.getState(), tid),
            );
            const voiceoverArgs: CreateVideoFromVoiceoverAssetPlanArgs = {
                audio_uuid: voiceoverAsset.asset_uuid,
                asset_kind: voiceoverAsset.asset_kind,
                title: validVideoPlan.title,
                aspect_ratio: validVideoPlan.aspect_ratio,
                template_id: validVideoPlan.template_id,
                blueprint_id: validVideoPlan.blueprint_id ?? undefined,
            };

            analytics.track('Sketch: Voiceover Video Plan Edited', {
                asset_kind: voiceoverAsset.asset_kind,
                changed_aspect_ratio:
                    validVideoPlan.aspect_ratio !== originalPlan.current.aspect_ratio,
                changed_template: validVideoPlan.template_id !== originalPlan.current.template_id,
                changed_blueprint:
                    validVideoPlan.blueprint_id !== originalPlan.current.blueprint_id,
            });

            void executeTool('create_video_from_voiceover_asset_plan', voiceoverArgs);
            setHasCreatedVideo(true);
            setCollapsed(true);
            return;
        }

        const valid = validateScript(script);
        if (!valid) return;

        const validVideoPlan = fillMissingVideoPlanFields(
            {
                script,
                aspectRatio,
                templateId,
                narrativeThread,
                voiceSlug,
                durationSeconds,
                blueprintId,
            },
            originalPlan.current,
            blueprint,
            (tid) => getSupportedAspectRatios(store.getState(), tid),
        );

        const args: CreateVideoFromPlanArgs = {
            title: validVideoPlan.title,
            script: validVideoPlan.script,
            aspect_ratio: validVideoPlan.aspect_ratio,
            template_id: validVideoPlan.template_id,
            narrative_thread: validVideoPlan.narrative_thread,
            ai_voice: validVideoPlan.ai_voice ?? undefined,
            duration_target_seconds: validVideoPlan.duration_target_seconds ?? undefined,
            blueprint_id: validVideoPlan.blueprint_id ?? undefined,
            video_prompt: validVideoPlan.video_prompt ?? undefined,
        };

        const changedScript = validVideoPlan.script !== originalPlan.current.script;
        const changedAspectRatio =
            validVideoPlan.aspect_ratio !== originalPlan.current.aspect_ratio;
        const changedTemplate = validVideoPlan.template_id !== originalPlan.current.template_id;
        const changedNarrativeThread =
            validVideoPlan.narrative_thread !== originalPlan.current.narrative_thread;
        const changedVoice = validVideoPlan.ai_voice !== originalPlan.current.ai_voice;
        const changedBlueprint = validVideoPlan.blueprint_id !== originalPlan.current.blueprint_id;

        analytics.track('Sketch: Video Plan Edited', {
            changed_script: changedScript,
            changed_aspect_ratio: changedAspectRatio,
            changed_template: changedTemplate,
            changed_narrative_thread: changedNarrativeThread,
            changed_voice: changedVoice,
            changed_blueprint: changedBlueprint,
            changed_any:
                changedScript ||
                changedAspectRatio ||
                changedTemplate ||
                changedNarrativeThread ||
                changedVoice ||
                changedBlueprint,
        });

        void executeTool('create_video_from_plan', args);
        setHasCreatedVideo(true);
        setCollapsed(true);
    }, [
        executeTool,
        validateScript,
        script,
        aspectRatio,
        templateId,
        narrativeThread,
        voiceSlug,
        durationSeconds,
        blueprintId,
        blueprint,
        store,
        voiceoverAsset,
    ]);

    const brandKitColorsArray = useMemo(
        () => (brandKitColors ? [...brandKitColors] : null),
        [brandKitColors],
    );
    const templateColorsArray = useMemo(() => {
        const firstPalette = templatePaletteColors && templatePaletteColors[0];
        if (!firstPalette) return null;
        return firstPalette.map((intColor) => `rgb(${int2rgb(intColor).join(',')})`);
    }, [templatePaletteColors]);

    const badgesState: SketchControlsBadgesState = useMemo(
        () => ({
            blueprintName: blueprint?.name ?? null,
            templateName,
            templateColors: brandKitName ? null : templateColorsArray,
            // Narrative thread and voice are fixed by the uploaded asset in the
            // voiceover-outline flow — hide those badges to avoid implying they
            // can be changed.
            narrativeThread: voiceoverAsset ? null : narrativeThread,
            durationSeconds: voiceoverAsset ? null : durationSeconds,
            aspectRatio,
            voiceName: voiceoverAsset ? null : voiceName,
            mediaSources: {
                stockMediaEnabled,
                damMediaEnabled,
                workspaceMediaMode,
                mediaGuidance,
                isDamAvailable,
            },
            brandKitName,
            brandKitColors: brandKitColorsArray,
        }),
        [
            blueprint?.name,
            templateName,
            templateColorsArray,
            narrativeThread,
            durationSeconds,
            aspectRatio,
            voiceName,
            stockMediaEnabled,
            damMediaEnabled,
            workspaceMediaMode,
            mediaGuidance,
            isDamAvailable,
            brandKitName,
            brandKitColorsArray,
            voiceoverAsset,
        ],
    );

    const scriptNode = voiceoverAsset ? (
        <div className="mb-l5-8">
            <div className="font-l5 font-l5--regular--semibold mb-l5-4">Transcript</div>
            <div
                className="font-l5 font-l5--small color-grey-700 overflow-y-auto"
                style={{
                    // ~4.5 lines of small text (lh ~1.4em)
                    maxHeight: '6.3em',
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                }}
            >
                {voiceoverAsset.transcript || script || '(no transcript)'}
            </div>
        </div>
    ) : (
        <>
            <div className="font-l5 font-l5--regular--semibold mb-l5-8">Script</div>
            <TextInput
                required
                resize="vertical"
                variant={TEXT_INPUT_VARIANTS.TEXTAREA}
                text={script}
                onChange={handleScriptChange}
                lines={Math.max(4, Math.min((script.match(/\n/g) || []).length + 2, 8))}
                formFeedbackMessage={scriptError}
                formFeedbackMessageType={FEEDBACK_MESSAGE_TYPES.ERROR}
            />
        </>
    );

    return (
        <SketchWidget
            title="Video outline"
            titleClassName="font-l5 font-l5--subtitle-l--semibold color-grey-600"
            buttonText="Create video"
            onClick={handleCreateVideo}
            collapsible={hasCreatedVideo}
            collapsed={collapsed}
            onToggleCollapse={() => setCollapsed((prev) => !prev)}
        >
            <>
                {scriptNode}

                <div className="d-flex flex-wrap gap-2 align-items-center mt-l5-16">
                    <div className="font-l5 font-l5--regular--semibold">Settings</div>
                    <SketchControls
                        source="widget"
                        dropdownDisabled={false}
                        badgesState={badgesState}
                        badgeSize={BADGE_SIZES.SM}
                        scrollable={false}
                        dropdownTogglePosition="inline-end"
                        hideDuration
                        hideNarrativeThread={!!voiceoverAsset}
                        hideVoice={!!voiceoverAsset}
                        selectedTemplateId={templateId}
                        selectedAIVoiceId={voiceSlug}
                        canUseAIVoiceover={canUseAIVoiceover}
                        brandKits={brandKits}
                        supportedAspectRatios={supportedAspectRatios}
                        onSelectControl={handleSelectControl}
                        onSelectBlueprint={handleSelectBlueprint}
                        onSelectTemplate={handleSelectTemplate}
                        onSelectVoice={handleSelectVoice}
                        onSelectDuration={handleSelectDuration}
                        onSelectAspectRatio={handleSelectAspectRatio}
                        onClearControl={handleClearControl}
                    />
                </div>
            </>
        </SketchWidget>
    );
};
